import React, { useEffect } from 'react';
import { OG_IMAGE_URL } from './config';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { LeadProvider } from './contexts/LeadContext';
import { usePageTracking } from './hooks/usePageTracking';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { PainPoints } from './components/PainPoints';
import { MeetTheQueens } from './components/MeetTheQueens';
import { QueenExperience } from './components/QueenExperience';
import { MostWanted } from './components/MostWanted';
import { TradeIn } from './components/TradeIn';
import { VideoSection } from './components/VideoSection';
import { LeadForm } from './components/LeadForm/LeadForm';
import { HIDE_LEAD_FORM_FOR_COMPLIANCE } from './config';
import { HowItWorks } from './components/HowItWorks';
import { Faq } from './components/Faq';
import { Footer } from './components/Footer';
import { SectionDivider } from './components/SectionDivider';
import { GHLWidget } from './components/GHLWidget';

/**
 * Head tags.
 *
 * robots: REMOVE BEFORE LAUNCH — placeholder content is currently live, so the page
 * must not be indexed yet.
 *
 * og:image: rendered only once OG_IMAGE_URL holds a real approved asset. Emitting a tag
 * that points nowhere makes WhatsApp and Meta render a broken preview card.
 */
function useHeadMeta() {
  useEffect(() => {
    const tags: HTMLMetaElement[] = [];
    const add = (attr: 'name' | 'property', key: string, content: string) => {
      const meta = document.createElement('meta');
      meta.setAttribute(attr, key);
      meta.content = content;
      document.head.appendChild(meta);
      tags.push(meta);
    };

    // REMOVE BEFORE LAUNCH
    add('name', 'robots', 'noindex, nofollow');
    if (OG_IMAGE_URL) {
      add('property', 'og:image', OG_IMAGE_URL);
      add('name', 'twitter:image', OG_IMAGE_URL);
      add('name', 'twitter:card', 'summary_large_image');
    }
    return () => {
      tags.forEach(tag => document.head.removeChild(tag));
    };
  }, []);
}
function Landing() {
  const {
    t
  } = useLanguage();
  usePageTracking();
  useHeadMeta();
  return <div className="w-full overflow-x-hidden bg-white" data-id="element-0" data-mp="3uc9jfxd5xojt">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:bg-ink focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white" data-id="element-1" data-mp="d57h17dyqql6">
        {t('nav.skip')}
      </a>
      <Header data-id="element-2" data-mp="3glnz048296jt" />
      <main id="main" data-id="element-3" data-mp="3gj1r23h9cp12">
        {/* One argument per screen, each ending in the next step:
            hook → who they are → why different → what to pick → your car →
            what happens next → give your data → objections handled. */}
        <VideoSection src="https://cdn.magicpatterns.com/uploads/d5VZi8boURW3RqnLtkGKAS/A_REAL_SUV_15_1920x796.mp4" fullBleed data-id="element-4" data-mp="3lk7ky4mnmic6" />
        <Hero data-id="element-5" data-mp="2xsk1u8vmwbkg" />
        <PainPoints data-id="element-6" data-mp="2svtr812lm86f" />
        <MeetTheQueens data-id="element-7" data-mp="2swll97gjrxul" />
        <SectionDivider data-id="element-8" data-mp="2sv0mf5n79sqx" />
        <QueenExperience data-id="element-9" data-mp="2stpkzch88x2z" />
        <MostWanted data-id="element-10" data-mp="18e1axny9tulm" />
        <TradeIn data-id="element-11" data-mp="18dhp3w5i2m2b" />
        <HowItWorks data-id="element-12" data-mp="18czoknbt1lol" />
        <VideoSection src="https://cdn.magicpatterns.com/uploads/xyHVoiyZwgDdy7V9eYcTtp/my27-telli-ice-hero-desktop_moov.mp4" data-id="element-13" data-mp="18cgc3m4xbphy" />
        {!HIDE_LEAD_FORM_FOR_COMPLIANCE && <LeadForm data-id="element-14" data-mp="ygmv9qk699ud" />}
        <SectionDivider data-id="element-15" data-mp="yh6f5ctdu8b1" />
        <Faq data-id="element-16" data-mp="ydquzvkuq92r" />
      </main>
      <Footer data-id="element-17" data-mp="13e55rldu7x4h" />
      <GHLWidget data-id="element-18" data-mp="13dwutz19z1vx" />
    </div>;
}
export function App() {
  return <LanguageProvider data-id="element-19" data-mp="1r495virtheqq">
      <LeadProvider data-id="element-20" data-mp="1r3zg9waop9lk">
        <Landing data-id="element-21" data-mp="1r5kblo4towkh" />
      </LeadProvider>
    </LanguageProvider>;
}